import 'react-notifications/lib/notifications.css'
import React, { Component } from 'react'
import { withTranslation } from 'react-i18next'
import { NotificationContainer, NotificationManager } from 'react-notifications'
import { useParams } from 'react-router-dom'
import Chart from "react-apexcharts"
import { FaChartBar, FaCoins, FaShoppingBag, FaUsers } from "react-icons/fa"
import AdminLayout from "../../../components/AdminLayout"

import CardService from "../../../services/card_services"
import PackForm from "./PackForm"

export function withRouter(Children) {
    return (props) => {
        const match = { params: useParams() }
        return <Children {...props} match={match} />
    }
}

class AdminUpdatePack extends Component {
    constructor(props) {
        super(props)
        this.state = {
            pack_uuid: null,
            initialPack: null,
            packStats: null,
            start_date: '',
            end_date: '',
            loading: true,
        }
    }

    componentDidMount = async () => {
        const pack_uuid = this.props.match.params.id
        this.setState({ pack_uuid })
        try {
            const data = await CardService.getPack(pack_uuid)
            const pack = (data.packs || [])[0]
            if (!pack) {
                NotificationManager.error('Pack not found')
                this.setState({ loading: false })
                return
            }
            const stats = data.pack_stats || null
            this.setState({
                initialPack: pack,
                packStats: stats,
                start_date: stats && stats.start_date ? stats.start_date.substring(0, 10) : '',
                end_date: stats && stats.end_date ? stats.end_date.substring(0, 10) : '',
                loading: false,
            })
        } catch (e) {
            this.setState({ loading: false })
            NotificationManager.error(e.response && e.response.data ? e.response.data.message : e.message)
        }
    }

    loadStats = async () => {
        const { pack_uuid, start_date, end_date } = this.state
        if (!start_date || !end_date) return
        try {
            const data = await CardService.getPack(pack_uuid, start_date, end_date)
            this.setState({ packStats: data.pack_stats || null })
        } catch (e) {
            NotificationManager.error(e.response && e.response.data ? e.response.data.message : e.message)
        }
    }

    handleSubmit = async (payload) => {
        return await CardService.updatePack(this.state.pack_uuid, payload)
    }

    formatNumber = (value) => (value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })

    renderStats = () => {
        const stats = this.state.packStats
        if (!stats) return null

        const dailySales = stats.daily_sales || {}
        const prices = stats.pack_prices_distribution || stats.prices_distribution || {}
        const dailyOptions = {
            chart: { id: 'pack-daily-sales' },
            xaxis: { categories: Object.keys(dailySales) },
            yaxis: [
                { title: { text: 'Sales' } },
                { opposite: true, title: { text: 'Assets' } },
            ],
        }
        const dailySeries = [
            { name: 'Sales', data: Object.values(dailySales).map(day => day.count || 0) },
            { name: 'Assets', data: Object.values(dailySales).map(day => day.nb_asset || 0) },
        ]
        const priceOptions = {
            chart: { id: 'pack-prices' },
            xaxis: { categories: Object.keys(prices) },
        }
        const priceSeries = [{ name: 'Purchases', data: Object.values(prices) }]
        const metrics = [
            { label: 'Purchases', value: stats.nb_actions, icon: FaShoppingBag, color: 'text-blue-600 bg-blue-50' },
            { label: 'Assets spent', value: stats.nb_assets, icon: FaCoins, color: 'text-amber-600 bg-amber-50' },
            { label: 'Unique players', value: stats.nb_unique_players, icon: FaUsers, color: 'text-green-600 bg-green-50' },
            { label: 'Assets / purchase', value: stats.assets_by_action, icon: FaChartBar, color: 'text-fuchsia-600 bg-fuchsia-50' },
            { label: 'Assets / player', value: stats.assets_by_player, icon: FaUsers, color: 'text-cyan-600 bg-cyan-50' },
        ]

        return (
            <div className="space-y-6 mb-6">
                <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <FaChartBar className="text-fuchsia-600" /> Pack Statistics
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {['start_date', 'end_date'].map(field => (
                            <div key={field}>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {field === 'start_date' ? 'Start Date' : 'End Date'}
                                </label>
                                <input
                                    type="date"
                                    value={this.state[field]}
                                    onChange={(event) => this.setState({ [field]: event.target.value }, this.loadStats)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fuchsia-500 outline-none"
                                />
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
                        {metrics.map(metric => {
                            const Icon = metric.icon
                            return (
                                <div key={metric.label} className="border border-gray-200 rounded-lg p-4">
                                    <Icon className={`text-2xl p-1 rounded ${metric.color}`} />
                                    <p className="text-xl font-bold text-gray-900 mt-3">{this.formatNumber(metric.value)}</p>
                                    <p className="text-xs font-semibold text-gray-500 uppercase mt-1">{metric.label}</p>
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Daily Sales</h4>
                        <Chart options={dailyOptions} series={dailySeries} type="line" />
                    </div>
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Price Distribution</h4>
                        <Chart options={priceOptions} series={priceSeries} type="bar" />
                    </div>
                </div>
            </div>
        )
    }

    render() {
        return (
            <>
                <AdminLayout
                    title="Update Pack / Booster"
                    breadcrumbs={[
                        { label: 'Packs / Boosters', href: '/admin/packs' },
                        { label: 'Update' }
                    ]}
                >
                    {this.state.loading ? (
                        <div className="text-center text-gray-500 py-10">Loading pack...</div>
                    ) : !this.state.initialPack ? (
                        <div className="text-center text-red-500 py-10">Pack not found.</div>
                    ) : (
                        <>
                            {this.renderStats()}
                            <PackForm
                                mode="update"
                                submitLabel="Save Changes"
                                initialPack={this.state.initialPack}
                                onSubmit={this.handleSubmit}
                            />
                        </>
                    )}
                </AdminLayout>
                <NotificationContainer />
            </>
        )
    }
}

export default withTranslation()(withRouter(AdminUpdatePack))
